/**
 * Вспомогательная функция: возвращает текущую дату/время по таймзоне Майами (America/New_York).
 * Используется везде вместо new Date(), чтобы все расчёты были привязаны к ET,
 * независимо от локали и таймзоны устройства пользователя (в т.ч. macOS/Safari).
 * @returns {Date}
 */
const getNowMiami = () => {
  return new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
};

/**
 * Вспомогательная функция: проверяет, что значение является валидным объектом Date.
 * Защита от Invalid Date, которые возникают в Safari при некорректном парсинге дат.
 * @param {*} d
 * @returns {boolean}
 */
const isValidDate = (d) => d instanceof Date && !isNaN(d);

/**
 * Generic function to calculate remaining stock after N weeks.
 * @param {number} initialStock - Stock at the beginning of current week
 * @param {string} identifier - ASIN/SKU
 * @param {number} targetWeek - Week number to calculate stock for (start of that week)
 * @param {number} targetYear - Year of target week
 * @param {Object} currentWeekInfo - { weekNumber, year }
 * @param {Function} calculateForecast - (id, week, year, growth) => number|'N/A'
 * @param {Function} calculateAverageGrowth - (id) => number
 * @param {Function} getWeeksBetween - (start, end) => array of {week, year}
 * @returns {number} Remaining stock at start of target week
 */
export const calculateStockRemainingAfterWeeks = (
  initialStock,
  identifier,
  targetWeek,
  targetYear,
  currentWeekInfo,
  calculateForecast,
  calculateAverageGrowth,
  getWeeksBetween
) => {
  let remainingStock = initialStock;

  // If target is current week, no subtraction needed
  if (targetWeek === currentWeekInfo?.weekNumber && targetYear === currentWeekInfo?.year) {
    return remainingStock;
  }

  const averageGrowth = calculateAverageGrowth(identifier);
  const weeksToSubtract = getWeeksBetween(
    { weekNumber: currentWeekInfo.weekNumber, year: currentWeekInfo.year },
    { weekNumber: targetWeek, year: targetYear }
  );

  weeksToSubtract.forEach(week => {
    const forecast = calculateForecast(identifier, week.weekNumber, week.year, averageGrowth);
    if (forecast !== 'N/A' && forecast > 0) {
      remainingStock -= forecast;
      if (remainingStock < 0) remainingStock = 0;
    }
  });

  return remainingStock;
};

// Функция для расчета Days Supplied
export const calculateDaysSupplied = (stock, asin, startWeek, startYear, 
                                      currentWeekInfo, calculateAverageGrowth, 
                                      calculateForecast, getSalesData) => {
    if (!stock || stock <= 0) return 0;
    
    const averageGrowth = calculateAverageGrowth(asin);
    let remainingStock = stock;
    let totalDays = 0;
    
    let currentWeek = startWeek || currentWeekInfo?.weekNumber;
    let currentYear = startYear || currentWeekInfo?.year;
    
    // Используем время Майами вместо локального времени устройства
    const today = getNowMiami();

    // Проверяем валидность дат перед сравнением (защита от Safari/Invalid Date)
    const mondayObj = currentWeekInfo?.mondayDateObj;
    const sundayObj = currentWeekInfo?.sundayDateObj;
    const isInCurrentWeek = isValidDate(mondayObj) &&
                            isValidDate(sundayObj) &&
                            startWeek === currentWeekInfo.weekNumber &&
                            startYear === currentWeekInfo.year &&
                            today >= mondayObj && 
                            today <= sundayObj;
    
    let weeksCounted = 0;
    const maxWeeks = 52;
    
    while (remainingStock > 0 && weeksCounted < maxWeeks) {
        const forecast = calculateForecast(asin, currentWeek, currentYear, averageGrowth);
        
        if (forecast === 'N/A' || forecast <= 0) {
            // Переход к следующей неделе
            currentWeek++;
            if (currentWeek > 52) {
                currentWeek = 1;
                currentYear++;
            }
            weeksCounted++;
            continue;
        }
        
        let daysInWeek = 7;
        
        // Если это первая неделя и мы внутри нее, учитываем только оставшиеся дни
        if (weeksCounted === 0 && isInCurrentWeek) {
            const daysPassed = Math.floor((today - mondayObj) / (1000 * 60 * 60 * 24));
            daysInWeek = Math.max(0, 7 - daysPassed);
        }
        
        const dailySales = forecast / 7;
        const salesThisWeek = dailySales * daysInWeek;
        
        // Если стока хватит на всю неделю
        if (remainingStock >= salesThisWeek) {
            totalDays += daysInWeek;
            remainingStock -= salesThisWeek;
        } else {
            // Если стока хватит только на часть недели
            const daysCovered = remainingStock / dailySales;
            totalDays += daysCovered;
            break;
        }
        
        // Переход к следующей неделе
        currentWeek++;
        if (currentWeek > 52) {
            currentWeek = 1;
            currentYear++;
        }
        weeksCounted++;
    }
    
    return Math.round(totalDays * 10) / 10;
};

// Функция для расчета Due Date на основе Days Supplied
export const calculateDueDate = (daysSupplied) => {
    if (daysSupplied === 0 || daysSupplied === 'N/A' || !daysSupplied) {
        return 'N/A';
    }
    
    try {
        // Используем время Майами вместо локального времени устройства
        const today = getNowMiami();
        const dueDate = new Date(today);
        dueDate.setDate(today.getDate() + Math.round(daysSupplied));
        
        // Форматируем в американский формат MM/DD/YYYY
        const month = String(dueDate.getMonth() + 1).padStart(2, '0');
        const day = String(dueDate.getDate()).padStart(2, '0');
        const year = dueDate.getFullYear();
        
        return `${month}/${day}/${year}`;
    } catch (error) {
        console.error('Error calculating due date:', error);
        return 'N/A';
    }
};

// Функция для получения CSS класса для Due Date
export const getDueDateClass = (daysSupplied) => {
    if (daysSupplied === 0 || daysSupplied === 'N/A' || !daysSupplied) {
        return 'due-date-na';
    }
    
    // Используем ту же логику цвета, что и для Days Supplied
    return getWeeksSuppliedClass(daysSupplied);
};

// Функция для получения класса цвета на основе количества дней (в пересчете на недели)
export const getWeeksSuppliedClass = (days) => {
    if (days === 0 || days === 'N/A' || !days) {
        return 'weeks-supplied-red'; // 0 дней - красный
    }
    
    // Преобразуем дни в недели
    const weeks = days / 7;
    
    // Определяем цвет по диапазонам недель
    if (weeks > 16) {
        return 'weeks-supplied-purple'; // Более 16 недель - фиолетовый
    } else if (weeks >= 10) {
        return 'weeks-supplied-green'; // 10-16 недель - зеленый
    } else if (weeks >= 4) {
        return 'weeks-supplied-yellow'; // 4-10 недель - желтый
    } else {
        return 'weeks-supplied-orange'; // 0-4 недели - оранжевый
    }
};

// Функция для получения класса цвета для ячейки прогноза на основе Days Supplied
export const getForecastCellClass = (asin, weekNumber, year, 
                                     calculateDaysSuppliedAtWeek) => {
    // Рассчитываем, сколько дней осталось до исчерпания стока на начало этой недели
    const daysSuppliedAtWeekStart = calculateDaysSuppliedAtWeek(asin, weekNumber, year);
    
    if (daysSuppliedAtWeekStart === 0 || daysSuppliedAtWeekStart === 'N/A' || !daysSuppliedAtWeekStart) {
        return 'weeks-supplied-red';
    }
    
    // Преобразуем дни в недели
    const weeksCovered = daysSuppliedAtWeekStart / 7;
    
    // Определяем цвет по тем же правилам, что и для Days Supplied
    if (weeksCovered >= 16) {
        return 'weeks-supplied-purple';
    } else if (weeksCovered >= 10) {
        return 'weeks-supplied-green';
    } else if (weeksCovered >= 4) {
        return 'weeks-supplied-yellow';
    } else {
        return 'weeks-supplied-orange';
    }
};

// Функция для расчета остатка FBA стока к началу указанной недели
export const calculateFBAStockAfterWeeks = (asin, targetWeek, targetYear,
                                            currentWeekInfo, productsData,
                                            calculateAverageGrowth, calculateForecast,
                                            getWeeksBetween) => {
    const product = productsData.find(p => p.asin === asin);
    if (!product) return 0;
    
    // Начальный FBA сток
    let remainingStock = product.fbaStock || 0;
    
    // Если целевая неделя - это текущая неделя, возвращаем начальный сток
    if (targetWeek === currentWeekInfo?.weekNumber && targetYear === currentWeekInfo?.year) {
        return remainingStock;
    }
    
    // Рассчитываем средний процент роста
    const averageGrowth = calculateAverageGrowth(asin);
    
    // Получаем список всех недель от текущей до целевой (исключая целевую)
    const weeksToSubtract = getWeeksBetween(
        { weekNumber: currentWeekInfo.weekNumber, year: currentWeekInfo.year },
        { weekNumber: targetWeek, year: targetYear }
    );
    
    // Вычитаем прогнозируемые продажи за каждую неделю
    weeksToSubtract.forEach(week => {
        const forecast = calculateForecast(asin, week.weekNumber, week.year, averageGrowth);
        if (forecast !== 'N/A' && forecast > 0) {
            remainingStock -= forecast;
            if (remainingStock < 0) remainingStock = 0;
        }
    });
    
    return remainingStock;
};

// Функция для расчета количества дней запаса на начало указанной недели
export const calculateDaysSuppliedAtWeek = (asin, weekNumber, year,
                                            calculateFBAStockAfterWeeks,
                                            calculateDaysSuppliedFromWeek) => {
    // Рассчитываем остаток FBA стока на начало этой недели
    const stockAtWeekStart = calculateFBAStockAfterWeeks(asin, weekNumber, year);
    
    // Если сток на начало недели <= 0, возвращаем 0
    if (stockAtWeekStart <= 0) return 0;
    
    // Рассчитываем, на сколько дней хватит этого остатка
    return calculateDaysSuppliedFromWeek(stockAtWeekStart, asin, weekNumber, year);
};

// Функция для расчета Days Supplied, начиная с указанной недели
export const calculateDaysSuppliedFromWeek = (stock, asin, startWeek, startYear,
                                              currentWeekInfo, calculateAverageGrowth,
                                              calculateForecast, DAYS_SUPPLIED_CALCULATION_WEEKS = 52) => {
    if (!stock || stock <= 0) return 0;
    
    const averageGrowth = calculateAverageGrowth(asin);
    let remainingStock = stock;
    let totalDays = 0;
    
    let currentWeek = startWeek;
    let currentYear = startYear;
    
    // Определяем, является ли startWeek текущей неделей
    const isCurrentWeek = currentWeekInfo && 
                        startWeek === currentWeekInfo.weekNumber && 
                        startYear === currentWeekInfo.year;
    
    // Если это текущая неделя, проверяем, сколько дней прошло
    let daysInFirstWeek = 7;
    if (isCurrentWeek) {
        // Используем время Майами вместо локального времени устройства
        const today = getNowMiami();
        const mondayObj = currentWeekInfo?.mondayDateObj;
        // Проверяем валидность даты перед использованием (защита от Safari/Invalid Date)
        if (isValidDate(mondayObj) && today >= mondayObj) {
            const daysPassed = Math.floor((today - mondayObj) / (1000 * 60 * 60 * 24));
            daysInFirstWeek = Math.max(0, 7 - daysPassed);
        }
    }
    
    let weeksCounted = 0;
    const maxWeeks = DAYS_SUPPLIED_CALCULATION_WEEKS; // По умолчанию 52 недели
    
    while (remainingStock > 0 && weeksCounted < maxWeeks) {
        const forecast = calculateForecast(asin, currentWeek, currentYear, averageGrowth);
        
        if (forecast === 'N/A' || forecast <= 0) {
            // Переход к следующей неделе
            currentWeek++;
            if (currentWeek > 52) {
                currentWeek = 1;
                currentYear++;
            }
            weeksCounted++;
            continue;
        }
        
        // Определяем, сколько дней учитывать в этой неделе
        let daysInWeek = 7;
        if (weeksCounted === 0 && isCurrentWeek) {
            daysInWeek = daysInFirstWeek;
        }
        
        const dailySales = forecast / 7;
        const salesThisWeek = dailySales * daysInWeek;
        
        // Если стока хватит на всю неделю
        if (remainingStock >= salesThisWeek) {
            totalDays += daysInWeek;
            remainingStock -= salesThisWeek;
        } else {
            // Если стока хватит только на часть недели
            const daysCovered = remainingStock / dailySales;
            totalDays += daysCovered;
            break;
        }
        
        // Переход к следующей неделе
        currentWeek++;
        if (currentWeek > 52) {
            currentWeek = 1;
            currentYear++;
        }
        weeksCounted++;
    }
    
    return Math.round(totalDays * 10) / 10;
};

// Функция для получения списка недель между двумя датами (исключая конечную)
export const getWeeksBetween = (startWeek, endWeek) => {
    const weeks = [];
    let currentWeek = startWeek.weekNumber;
    let currentYear = startWeek.year;
    
    // Пока не достигнем целевой недели (не включая ее)
    while (!(currentYear === endWeek.year && currentWeek === endWeek.weekNumber)) {
        // Добавляем текущую неделю
        weeks.push({ weekNumber: currentWeek, year: currentYear });
        
        // Переходим к следующей неделе
        currentWeek++;
        if (currentWeek > 52) {
            currentWeek = 1;
            currentYear++;
        }
        
        // Если перешли через год и нужно остановиться
        if (currentYear > endWeek.year || (currentYear === endWeek.year && currentWeek > endWeek.weekNumber)) {
            break;
        }
    }
    
    return weeks;
};

// Функция для расчета остатка стока к началу указанной недели
export const calculateStockAfterWeeks = (asin, targetWeek, targetYear,
                                         currentWeekInfo, productsData,
                                         calculateAverageGrowth, calculateForecast,
                                         getWeeksBetween) => {
    const product = productsData.find(p => p.asin === asin);
    if (!product) return 0;
    
    // Начальный сток
    let remainingStock = (product.fbaStock || 0) + (product.whStock || 0);
    
    // Если целевая неделя - это текущая неделя, возвращаем начальный сток
    if (targetWeek === currentWeekInfo?.weekNumber && targetYear === currentWeekInfo?.year) {
        return remainingStock;
    }
    
    // Рассчитываем средний процент роста
    const averageGrowth = calculateAverageGrowth(asin);
    
    // Получаем список всех недель от текущей до целевой (исключая целевую)
    const weeksToSubtract = getWeeksBetween(
        { weekNumber: currentWeekInfo.weekNumber, year: currentWeekInfo.year },
        { weekNumber: targetWeek, year: targetYear }
    );
    
    // Вычитаем прогнозируемые продажи за каждую неделю
    weeksToSubtract.forEach(week => {
        const forecast = calculateForecast(asin, week.weekNumber, week.year, averageGrowth);
        if (forecast !== 'N/A' && forecast > 0) {
            remainingStock -= forecast;
            if (remainingStock < 0) remainingStock = 0;
        }
    });
    
    return remainingStock;
};

// Функция для расчета цвета на основе остатка стока и прогнозов
export const calculateColorByWeeks = (remainingStock, asin,
                                      currentWeekInfo, calculateAverageGrowth,
                                      calculateForecast) => {
    if (remainingStock <= 0) return 'weeks-supplied-red';
    
    const averageGrowth = calculateAverageGrowth(asin);
    let weeksCovered = 0;
    let tempStock = remainingStock;
    
    // Начинаем с текущей недели
    let weekNumber = currentWeekInfo?.weekNumber;
    let year = currentWeekInfo?.year;
    
    // Максимальное количество недель для расчета
    const maxWeeks = 104; // 2 года
    
    while (tempStock > 0 && weeksCovered < maxWeeks) {
        const forecast = calculateForecast(asin, weekNumber, year, averageGrowth);
        
        // Если нет прогноза, пропускаем неделю
        if (forecast === 'N/A' || forecast <= 0) {
            weekNumber++;
            if (weekNumber > 52) {
                weekNumber = 1;
                year++;
            }
            weeksCovered++;
            continue;
        }
        
        // Если стока хватит на полную неделю
        if (tempStock >= forecast) {
            tempStock -= forecast;
            weeksCovered++;
        } else {
            // Если стока хватит только на часть недели
            const fraction = tempStock / forecast;
            weeksCovered += fraction;
            break;
        }
        
        // Переход к следующей неделе
        weekNumber++;
        if (weekNumber > 52) {
            weekNumber = 1;
            year++;
        }
    }
    
    // Определяем цвет на основе количества недель
    if (weeksCovered >= 16) {
        return 'weeks-supplied-purple';
    } else if (weeksCovered >= 10) {
        return 'weeks-supplied-green';
    } else if (weeksCovered >= 4) {
        return 'weeks-supplied-yellow';
    } else if (weeksCovered > 0) {
        return 'weeks-supplied-orange';
    } else {
        return 'weeks-supplied-red';
    }
};

// Функция для получения прогнозных недель с правильной обработкой переходов через год
export const getExtendedForecastWeeks = (startWeek, startYear, count, getDateOfISOWeek) => {
    const weeks = [];
    
    for (let i = 0; i < count; i++) {
        let week = startWeek + i;
        let year = startYear;
        
        // Обработка перехода через конец года
        while (week > 52) {
            week -= 52;
            year += 1;
        }
        
        // Получаем даты недели
        const mondayDate = getDateOfISOWeek(year, week);
        const sundayDate = new Date(mondayDate);
        sundayDate.setDate(mondayDate.getDate() + 6);
        
        weeks.push({
            weekNumber: week,
            year: year,
            label: `W${week} ${year}`,
            isPrediction: true,
            mondayDateObj: mondayDate,
            sundayDateObj: sundayDate
        });
    }
    
    return weeks;
};

// Функция для отображения ячеек Days Supplied с цветовым кодированием
export const renderDaysSuppliedCell = (days) => {
    if (days === 0 || days === 'N/A' || !days) {
        return <span className="days-supplied-na">N/A</span>;
    }
    
    const displayValue = Math.round(days); // Округляем до целого
    const dayText = displayValue === 1 ? 'day' : 'days';
    
    // Получаем класс цвета на основе недель
    const colorClass = getWeeksSuppliedClass(days);
    
    return (
        <div className={`days-supplied-value ${colorClass}`}>
            {displayValue} {dayText}
        </div>
    );
};

// Функция для расчета остатка Amazon стока (FBA + AWD) к началу указанной недели
export const calculateAmazonStockAfterWeeks = (asin, targetWeek, targetYear,
                                            currentWeekInfo, productsData,
                                            calculateAverageGrowth, calculateForecast,
                                            getWeeksBetween) => {
  const product = productsData.find(p => p.asin === asin);
  if (!product) return 0;
  
  // Начальный Amazon сток (FBA + AWD)
  let remainingStock = (product.fbaStock || 0) + (product.awdStock || 0);
  
  // Если целевая неделя - это текущая неделя, возвращаем начальный сток
  if (targetWeek === currentWeekInfo?.weekNumber && targetYear === currentWeekInfo?.year) {
    return remainingStock;
  }
  
  // Рассчитываем средний процент роста
  const averageGrowth = calculateAverageGrowth(asin);
  
  // Получаем список всех недель от текущей до целевой (исключая целевую)
  const weeksToSubtract = getWeeksBetween(
    { weekNumber: currentWeekInfo.weekNumber, year: currentWeekInfo.year },
    { weekNumber: targetWeek, year: targetYear }
  );
  
  // Вычитаем прогнозируемые продажи за каждую неделю
  weeksToSubtract.forEach(week => {
    const forecast = calculateForecast(asin, week.weekNumber, week.year, averageGrowth);
    if (forecast !== 'N/A' && forecast > 0) {
      remainingStock -= forecast;
      if (remainingStock < 0) remainingStock = 0;
    }
  });
  
  return remainingStock;
};


/**
 * ============================================================
 *  ДИАГНОСТИКА: yoyCalculations — проблема с датами на Safari
 *  Вставить целиком в консоль браузера (F12 → Console)
 *  и прислать скриншот результата
 * ============================================================
 */

console.log('========================================');
console.log('   YOY CALC DIAGNOSTICS — START');
console.log('========================================');

// --- 1. Информация об устройстве и браузере ---
console.group('1. Device & Browser Info');
console.log('User Agent       :', navigator.userAgent);
console.log('Platform         :', navigator.platform);
console.log('Language         :', navigator.language);
console.log('Timezone (device):', Intl.DateTimeFormat().resolvedOptions().timeZone);
console.groupEnd();

// --- 2. Проверка new Date() ---
console.group('2. Date Parsing Tests');

const d1 = new Date();
console.log('new Date()                    :', d1.toString(), '| valid:', !isNaN(d1));

// Этот формат ломается в Safari — возвращает Invalid Date
const d2 = new Date('2025-03-03');
console.log("new Date('2025-03-03')        :", d2.toString(), '| valid:', !isNaN(d2));

// Этот формат работает везде включая Safari
const d3 = new Date(2025, 2, 3); // month is 0-indexed
console.log('new Date(2025, 2, 3)          :', d3.toString(), '| valid:', !isNaN(d3));

// Формат через toLocaleString + таймзона Майами (новый подход)
const d4 = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
console.log('getNowMiami()                 :', d4.toString(), '| valid:', !isNaN(d4));

console.groupEnd();

// --- 3. Симуляция isValidDate ---
console.group('3. isValidDate Guard Tests');


console.log('isValidDate(new Date())       :', isValidDate(new Date()));       // ожидаем true
console.log('isValidDate(Invalid Date)     :', isValidDate(new Date('bad'))); // ожидаем false
console.log("isValidDate('2025-03-03')     :", isValidDate(new Date('2025-03-03'))); // false в Safari!
console.log('isValidDate(new Date(2025,2,3)):', isValidDate(new Date(2025, 2, 3))); // ожидаем true

console.groupEnd();

// --- 4. Симуляция currentWeekInfo как он приходит из приложения ---
console.group('4. currentWeekInfo Simulation');

// Воспроизводим, как getDateOfISOWeek создаёт даты в yoyPredictionCalculations
const getDateOfISOWeek = (year, week) => {
  const simple = new Date(year, 0, 1 + (week - 1) * 7);
  const dow = simple.getDay();
  const ISOweekStart = simple;
  if (dow <= 4) {
    ISOweekStart.setDate(simple.getDate() - simple.getDay() + 1);
  } else {
    ISOweekStart.setDate(simple.getDate() + 8 - simple.getDay());
  }
  return ISOweekStart;
};

const now = new Date();
const currentYear = now.getFullYear();
// Простой расчёт номера недели
const startOfYear = new Date(Date.UTC(currentYear, 0, 1));
const weekNumber = Math.ceil(((now - startOfYear) / 86400000 + startOfYear.getUTCDay() + 1) / 7);

const mondayDateObj = getDateOfISOWeek(currentYear, weekNumber);
const sundayDateObj = new Date(mondayDateObj);
sundayDateObj.setDate(mondayDateObj.getDate() + 6);

console.log('Calculated week #            :', weekNumber);
console.log('mondayDateObj                :', mondayDateObj.toString(), '| valid:', isValidDate(mondayDateObj));
console.log('sundayDateObj                :', sundayDateObj.toString(), '| valid:', isValidDate(sundayDateObj));

const simulatedWeekInfo = {
  weekNumber,
  year: currentYear,
  mondayDateObj,
  sundayDateObj,
};

console.groupEnd();

// --- 5. Главный тест: isInCurrentWeek — OLD vs NEW ---
console.group('5. isInCurrentWeek: OLD vs NEW logic');

const today_device = new Date();
const today_miami  = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));

console.log('today (device local)         :', today_device.toString());
console.log('today (Miami/ET)             :', today_miami.toString());

// Старая логика (без isValidDate, без Miami)
const old_isInCurrentWeek =
  simulatedWeekInfo?.mondayDateObj &&
  weekNumber === simulatedWeekInfo.weekNumber &&
  currentYear === simulatedWeekInfo.year &&
  today_device >= simulatedWeekInfo.mondayDateObj &&
  today_device <= simulatedWeekInfo.sundayDateObj;

// Новая логика (с isValidDate + Miami)
const new_isInCurrentWeek =
  isValidDate(simulatedWeekInfo?.mondayDateObj) &&
  isValidDate(simulatedWeekInfo?.sundayDateObj) &&
  weekNumber === simulatedWeekInfo.weekNumber &&
  currentYear === simulatedWeekInfo.year &&
  today_miami >= simulatedWeekInfo.mondayDateObj &&
  today_miami <= simulatedWeekInfo.sundayDateObj;

console.log('OLD isInCurrentWeek          :', old_isInCurrentWeek);
console.log('NEW isInCurrentWeek          :', new_isInCurrentWeek);

if (old_isInCurrentWeek !== new_isInCurrentWeek) {
  console.warn('РЕЗУЛЬТАТ ОТЛИЧАЕТСЯ — баг подтверждён на этом устройстве');
} else {
  console.log('Результаты совпадают — на этом устройстве баг не воспроизводится');
}

console.groupEnd();

// --- 6. Итог ---
console.group('6. Summary');
const safariLike = !isValidDate(new Date('2025-03-03'));
console.log('Safari-style date parsing bug:', safariLike ? 'ДА — баг воспроизводится' : 'НЕТ — дата парсится корректно');
console.log('Miami time works             :', isValidDate(today_miami) ? 'ДА' : 'НЕТ');
console.log('isValidDate guard works      :', !isValidDate(new Date('bad')) ? 'ДА' : 'НЕТ');
console.groupEnd();

console.log('========================================');
console.log('   YOY CALC DIAGNOSTICS — END');
console.log('========================================');